import {
  Box,
  createStyles,
  IconButton,
  InputBase,
  InputLabel,
  makeStyles,
  Select,
  Theme,
  withStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from 'clsx';
import { CSSProperties, FC, ReactNode, useId } from 'react';
import { WbSelectInlineHeader } from './WbSelectInlineHeader';
import { getFocusColorFromTheme } from '../../styles';

interface Props {
  onChange: (value: string) => void;
  title: ReactNode;
  onClear?: () => void;
  value?: string;
  menuTitle?: string;
  style?: CSSProperties;
  disabled?: boolean;
  children: ReactNode;
  placeholder?: string;
  displayEmpty?: boolean;
  renderValue?: (value: any) => ReactNode;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      position: 'relative',
    },
    divider: {
      height: '20px',
      width: '1px',
      pointerEvents: 'none',
      backgroundColor: theme.palette.divider,
    },
    iconContainer: {
      position: 'absolute',
      right: 0,
      bottom: 0,
      top: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingInline: theme.spacing(1),
      gap: theme.spacing(1),
      pointerEvents: 'none',
    },
    icon: {
      fontSize: '13px',
      color: theme.palette.secondary.main,
      position: 'static',
      backgroundColor: 'transparent',
    },
    menu: {
      paddingInline: theme.spacing(1),
      minWidth: '240px',
      backgroundColor: theme.palette.white,
    },
    menuPopover: {
      boxShadow: theme.shadows[2],
    },
    selectWrapper: {
      position: 'relative',
      display: 'inline-flex',
    },
    clearButton: {
      position: 'absolute',
      right: 38,
      top: '50%',
      transform: 'translateY(-50%)',
      padding: 2,
      zIndex: 1,
      color: theme.palette.secondary.main,
    },
    clearIcon: {
      fontSize: 12,
    },
  }),
  { name: 'WbSelectInline' },
);

const CustomSelectIconComponent = (props: any) => {
  const classes = useStyles();
  return (
    <Box className={classes.iconContainer}>
      <Box className={classes.divider} />
      <KeyboardArrowDownIcon className={clsx(props.className, classes.icon)} />
    </Box>
  );
};

const CustomInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      // define here to have higher priority than MUI class
      '& > $input': {
        paddingRight: '38px',
      },
      width: '150px',
      '&$disabled': {
        opacity: 0.5,
        '& > $input > p': {
          color: 'rgba(0, 0, 0, 0.6)',
        },
      },
    },
    disabled: {},
    input: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.white,
      border: `1px solid ${theme.palette.grey[300]}`,
      display: 'flex',
      alignItems: 'center',
      padding: 0,
      paddingLeft: 8,
      height: 28,
      boxSizing: 'border-box',
      ...theme.typography.body1,
      color: theme.palette.secondary.dark,
      '& p': {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        paddingRight: '0',
        fontWeight: theme.typography.fontWeightMedium as number,
      },
      '&:focus': {
        borderRadius: 4,
        backgroundColor: theme.palette.white,
        borderColor: getFocusColorFromTheme(theme),
        borderWidth: '2px',
      },
    },
  }),
)(InputBase);

export const WbSelectInline: FC<Props> = ({
  onChange,
  onClear,
  title,
  value,
  children,
  style,
  disabled,
  menuTitle,
  placeholder,
  displayEmpty,
  renderValue,
}) => {
  const classes = useStyles();
  const labelId = useId();
  const showClear = !!onClear && !!value;

  return (
    <Box className={classes.container}>
      <InputLabel id={labelId} color="secondary">
        {title}
      </InputLabel>
      <Select
        labelId={labelId}
        value={value}
        placeholder={placeholder}
        inputProps={{
          style: showClear ? { paddingRight: 58 } : undefined,
        }}
        IconComponent={CustomSelectIconComponent}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          PaperProps: {
            className: classes.menuPopover,
          },
          MenuListProps: { className: classes.menu },
          getContentAnchorEl: null,
        }}
        input={<CustomInput />}
        onChange={(e: any) => {
          onChange(e.target.value);
        }}
        style={style}
        disabled={disabled}
        displayEmpty={displayEmpty}
        renderValue={renderValue}
      >
        {menuTitle && <WbSelectInlineHeader>{menuTitle}</WbSelectInlineHeader>}
        {children}
        {showClear && (
          <IconButton
            aria-label="clear selection"
            className={classes.clearButton}
            size="small"
            onClick={e => {
              e.stopPropagation();
              onClear();
            }}
          >
            <CloseIcon className={classes.clearIcon} />
          </IconButton>
        )}
      </Select>
    </Box>
  );
};
